import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { getHomeworkAnswers, getReviewArchive, reviewAnswer } from '../../services/adminService';
import Collapse from '../../components/Common/Collapse';
import { FileIcon, fmtSize } from '../../components/Common/fileKind';
import { avatarColor, AvatarIcon } from '../../utils/avatar';
import toast from 'react-hot-toast';

/* Проверка ДЗ.

   Очередь собрана работами, а не отдельными ответами: карточка — это ученик
   плюс занятие, а внутри все номера разом. Так виден контекст: где автопроверка
   уже вынесла вердикт, а где ждут человека. Баллы за номер ставит проверяющий,
   к каждому можно оставить комментарий — он придёт ученику вместе с оценкой.
   Рядом с колокольчиком — архив: те же работы, но уже проверенные. */

const IcCheck = ({ w = 2.4 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const IcX = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IcChev = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const IcArchive = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2.5" y="4" width="19" height="4.5" rx="1.4" /><path d="M4.5 8.5V19a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5V8.5" /><line x1="10" y1="13" x2="14" y2="13" />
  </svg>
);
const IcBack = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);

const ST_LABEL = {
  pending: 'ждёт проверки',
  correct: 'зачтено',
  incorrect: 'не зачтено',
  not_submitted: 'нет ответа',
};

/* Автопроверка баллы в базу не пишет — «верно» само по себе значит полный балл.
   Считаем так же, как уведомление ученику, иначе суммы разъезжались бы. */
const effScore = (it) => (it.score != null ? it.score
  : it.status === 'correct' ? it.maxScore
    : it.status === 'incorrect' ? 0 : null);

/* «ДЗ · ДЗ · Геометрия» выглядело глупо: названия уроков часто уже начинаются
   с «ДЗ ·» или «Вариант», поэтому приставку добавляем только когда её нет. */
const lessonLabel = (w) => {
  const title = (w.lesson?.title || '—').trim();
  const kind = w.lesson?.isVariant ? 'Вариант' : 'ДЗ';
  const head = title.toLowerCase().startsWith(kind.toLowerCase()) ? title : `${kind} · ${title}`;
  return w.sectionName ? `${head} · ${w.sectionName}` : head;
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'только что';
  if (m < 60) return `${m} мин назад`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ч назад`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'вчера';
  if (d < 30) return `${d} дн назад`;
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
};

/* Один номер работы. Ручные и непроверенные раскрыты сразу — с ними и работают;
   автоматически зачтённые свёрнуты, чтобы не мешать. */
function Item({ item, onSaved }) {
  const [score, setScore] = useState(item.score ?? '');
  const [comment, setComment] = useState(item.reviewComment || '');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);   // номера по умолчанию свёрнуты
  const eff = effScore(item);

  const save = async (status) => {
    if (!item.answerId || saving) return;
    if (!status && score === '' && !comment.trim()) {
      toast.error('Поставьте баллы или нажмите «Зачесть»');
      return;
    }
    const body = { comment };
    if (status) body.status = status;
    if (score !== '') body.score = Math.max(0, Math.min(item.maxScore, Number(score) || 0));
    // «Зачесть» без баллов = полный балл, «не зачесть» без баллов = ноль
    if (status && score === '') body.score = status === 'correct' ? item.maxScore : 0;

    setSaving(true);
    try {
      await reviewAnswer(item.answerId, body);
      const nextScore = body.score ?? null;
      setScore(nextScore ?? '');
      onSaved(item.homeworkId, {
        score: nextScore,
        reviewComment: comment || null,
        status: body.status || (nextScore > 0 ? 'correct' : 'incorrect'),
      });
      toast.success('Сохранено');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rv-item st-${item.status}${open ? ' open' : ''}`}>
      <button type="button" className="rv-row" onClick={() => setOpen(o => !o)}>
        <span className="rv-num">{item.number}</span>
        <span className="rv-row-main">
          <span className="rv-row-title">{item.title}</span>
          <span className="rv-row-tags">
            {item.manual && <span className="rv-tag manual">ручная</span>}
            <span className={`rv-tag s-${item.status}`}>{ST_LABEL[item.status]}</span>
          </span>
        </span>
        <span className="rv-row-score">
          {eff != null ? <b>{eff}</b> : <i>—</i>}<span>/&nbsp;{item.maxScore}</span>
        </span>
        <span className={`rv-chev${open ? ' up' : ''}`}><IcChev /></span>
      </button>

      <Collapse open={open}>
        <div className="rv-body">
          {item.description && (
            <div className="rv-desc" dangerouslySetInnerHTML={{ __html: item.description }} />
          )}

          <div className="rv-pair">
            <div className="rv-field">
              <div className="rv-cap">{item.manual ? 'Решение ученика' : 'Ответ ученика'}</div>
              <div className="rv-answer">{item.answer ? item.answer : <i>пусто</i>}</div>
            </div>
            {!item.manual && (
              <div className="rv-field">
                <div className="rv-cap">Правильный</div>
                <div className="rv-answer ok">{item.correctAnswer || '—'}</div>
              </div>
            )}
          </div>

          {!!item.files.length && (
            <div className="rv-files">
              {item.files.map((f, i) => (
                <a key={i} className="rv-file" href={f.url} target="_blank" rel="noopener noreferrer">
                  <span className="rv-file-ic"><FileIcon name={f.name} size={18} /></span>
                  <span className="rv-file-name">{f.name}</span>
                  {fmtSize(f.size) && <span className="rv-file-size">{fmtSize(f.size)}</span>}
                </a>
              ))}
            </div>
          )}

          {item.comment && (
            <div className="rv-note"><b>Комментарий ученика:</b> {item.comment}</div>
          )}

          {item.answerId ? (
            <div className="rv-grade">
              <textarea className="rv-comment" value={comment} maxLength={2000} rows={3}
                onChange={e => setComment(e.target.value)}
                placeholder="Комментарий ученику — что не так или за что молодец" />
              <div className="rv-grade-bar">
                <label className="rv-score">
                  <span>Баллы</span>
                  <input type="number" min="0" max={item.maxScore} value={score} placeholder="0"
                    onChange={e => setScore(e.target.value)} />
                  <span className="rv-of">из {item.maxScore}</span>
                </label>
                <div className="rv-acts">
                  <button type="button" className="rv-ok" disabled={saving} onClick={() => save('correct')}>
                    <IcCheck />Зачесть
                  </button>
                  <button type="button" className="rv-no" disabled={saving} onClick={() => save('incorrect')}>
                    <IcX />Не зачесть
                  </button>
                  <button type="button" className="rv-save" disabled={saving} onClick={() => save(null)}>
                    Сохранить
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="rv-none">Ученик не отвечал на этот номер</div>
          )}
        </div>
      </Collapse>
    </div>
  );
}

/* Номера работы. В варианте их бывает под тридцать, а ждут человека два —
   поэтому у длинных работ по умолчанию показываем только непроверенные,
   а «Все номера» рядом, одним кликом. */
function WorkItems({ work, onSaved }) {
  const pending = work.items.filter(it => it.status === 'pending');
  const [onlyPending, setOnlyPending] = useState(work.items.length > 10 && pending.length > 0);
  const list = onlyPending ? pending : work.items;

  return (
    <div className="rv-items">
      {work.items.length > 10 && pending.length > 0 && (
        <div className="rv-filter">
          <button type="button" className={onlyPending ? 'on' : ''} onClick={() => setOnlyPending(true)}>На проверке</button>
          <button type="button" className={onlyPending ? '' : 'on'} onClick={() => setOnlyPending(false)}>Все номера</button>
        </div>
      )}
      {list.map(it => (
        <Item key={it.homeworkId} item={it} onSaved={onSaved} />
      ))}
    </div>
  );
}

function WorkCard({ work, open, onToggle, onSaved, archive }) {
  const uid = work.user?.id || 0;
  const done = work.pending === 0;
  const got = work.items.reduce((sum, it) => sum + (effScore(it) || 0), 0);
  const max = work.items.reduce((sum, it) => sum + it.maxScore, 0);

  return (
    <div className={`rev-card${open ? ' open' : ''}${done && !archive ? ' done' : ''}`}>
      <button type="button" className="rv-head" onClick={onToggle}>
        <span className="rv-ava" style={{ background: avatarColor(uid) }}>
          {work.user?.avatar ? <img src={work.user.avatar} alt="" /> : <AvatarIcon id={uid} size={24} />}
        </span>
        <span className="rv-head-main">
          <span className="rv-head-name">{work.user?.name || 'Ученик'}</span>
          <span className="rv-head-meta">{lessonLabel(work)}</span>
        </span>
        <span className="rv-head-right">
          <span className="rv-head-sum">{got}<i>/{max}</i></span>
          {archive
            ? <span className="rv-tag s-correct">проверено</span>
            : done
              ? <span className="rv-tag s-correct">проверено</span>
              : <span className="rv-tag s-pending">{work.pending} на проверке</span>}
          <span className="rv-head-when">{timeAgo(archive ? work.reviewedAt : work.submittedAt)}</span>
          <span className={`rv-chev${open ? ' up' : ''}`}><IcChev /></span>
        </span>
      </button>

      {/* Высоту ограничиваем только на время самой анимации открытия/закрытия:
          номера внутри раскрываются своим Collapse, и внешнее ограничение
          заставляло карточку прыгать рывком на 300+ пикселей. */}
      <Collapse open={open} keepMounted={false}>
        <WorkItems work={work} onSaved={onSaved} />
      </Collapse>
    </div>
  );
}

const PAGE = 30;

const ReviewPage = () => {
  const [tab, setTab] = useState('queue');          // queue | archive
  const [works, setWorks] = useState([]);
  const [archive, setArchive] = useState([]);
  const [archiveTotal, setArchiveTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  // Переезд «очередь ⇄ архив» — тем же сдвигом, что в разделе «Ученики»
  const queuePaneRef = useRef(null);
  const archivePaneRef = useRef(null);
  const [swipeH, setSwipeH] = useState(0);
  const [swiping, setSwiping] = useState(false);

  useEffect(() => {
    getHomeworkAnswers()
      .then(r => {
        const list = r.data || [];
        setWorks(list);
        setOpenKey(list[0]?.key || null);
      })
      .catch(() => toast.error('Не удалось загрузить очередь'))
      .finally(() => setLoading(false));
  }, []);

  const loadArchive = useCallback(() => {
    if (archive.length) return;
    getReviewArchive(PAGE, 0)
      .then(r => { setArchive(r.data || []); setArchiveTotal(r.total || 0); })
      .catch(() => toast.error('Не удалось загрузить архив'));
  }, [archive.length]);

  /* Очередь и архив лежат рядом в одном «окне» и переезжают сдвигом — как
     активные ⇄ архив в «Учениках». Высоту окна ведём за видимой полосой,
     иначе страница прыгала бы на время анимации. */
  const toggleArchive = () => {
    const toArchive = tab !== 'archive';
    if (toArchive) loadArchive();
    setSwiping(true);
    setTab(toArchive ? 'archive' : 'queue');
    setOpenKey(toArchive ? null : (works[0]?.key || null));
    setTimeout(() => setSwiping(false), 520);
  };

  const loadMore = () => {
    setMore(true);
    getReviewArchive(PAGE, archive.length)
      .then(r => setArchive(a => [...a, ...(r.data || [])]))
      .catch(() => toast.error('Не удалось догрузить'))
      .finally(() => setMore(false));
  };

  // После сохранения правим номер на месте — список не должен прыгать под курсором
  const patchItem = (workKey, inArchive) => (homeworkId, patch) => {
    const apply = (ws) => ws.map(w => {
      if (w.key !== workKey) return w;
      const items = w.items.map(it => (it.homeworkId === homeworkId ? { ...it, ...patch } : it));
      return { ...w, items, pending: items.filter(x => x.status === 'pending').length };
    });
    if (inArchive) setArchive(apply); else setWorks(apply);
  };

  const plural = (n) => {
    const t = n % 100, o = n % 10;
    if (t >= 11 && t <= 14) return 'работ ждут';
    if (o === 1) return 'работа ждёт';
    if (o >= 2 && o <= 4) return 'работы ждут';
    return 'работ ждут';
  };
  const nums = (n) => {
    const t = n % 100, o = n % 10;
    if (t >= 11 && t <= 14) return 'номеров';
    if (o === 1) return 'номер';
    if (o >= 2 && o <= 4) return 'номера';
    return 'номеров';
  };
  const worksWord = (n) => {
    const t = n % 100, o = n % 10;
    if (t >= 11 && t <= 14) return 'работ';
    if (o === 1) return 'работа';
    if (o >= 2 && o <= 4) return 'работы';
    return 'работ';
  };

  const isArchive = tab === 'archive';
  const pendingTotal = works.reduce((s, w) => s + w.pending, 0);

  // высоту окна ведём за видимой полосой — иначе страница прыгает при переезде
  useLayoutEffect(() => {
    const measure = () => {
      const el = isArchive ? archivePaneRef.current : queuePaneRef.current;
      if (el) setSwipeH(el.offsetHeight);
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) [queuePaneRef.current, archivePaneRef.current].forEach(el => el && ro.observe(el));
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  });

  const shelf = (items, inArchive) => (
    items.length === 0 ? (
      <div className="rev-empty">
        <span className="rev-empty-ic"><IcCheck w={2} /></span>
        <div>{inArchive ? 'Архив пока пуст — здесь будут проверенные работы.' : 'Все ответы проверены — новых нет.'}</div>
      </div>
    ) : (
      <>
        <div className="rev-list">
          {items.map(w => (
            <WorkCard
              key={w.key}
              work={w}
              archive={inArchive}
              open={openKey === w.key}
              onToggle={() => setOpenKey(openKey === w.key ? null : w.key)}
              onSaved={patchItem(w.key, inArchive)}
            />
          ))}
        </div>
        {inArchive && archive.length < archiveTotal && (
          <button type="button" className="rv-more" disabled={more} onClick={loadMore}>
            {more ? 'Загружаем…' : `Показать ещё · осталось ${archiveTotal - archive.length}`}
          </button>
        )}
      </>
    )
  );

  return (
    <div>
      <div className="rv-top">
        <div>
          <h1 className="s-page-title">{isArchive ? 'Архив проверок' : 'Проверка ДЗ'}</h1>
          <p className="s-page-sub">
            {loading ? 'Загрузка…'
              : isArchive ? `${archiveTotal} ${worksWord(archiveTotal)} уже проверено`
                : works.length ? `${works.length} ${plural(works.length)} проверки · ${pendingTotal} ${nums(pendingTotal)}`
                  : 'Очередь пуста'}
          </p>
        </div>
        {/* Кнопка живёт рядом с колокольчиком, а он — в .main-content, не в
            содержимом страницы. Поэтому рисуем её порталом в тот же слой:
            иначе absolute считался бы от другого предка и кнопка уезжала. */}
        {createPortal(
          <button type="button"
            className={`dash-drafts-btn rv-arch-btn${isArchive ? ' on' : ''}`}
            onClick={toggleArchive}
            title={isArchive ? 'Вернуться к очереди' : 'Архив проверок'}
            aria-label={isArchive ? 'Вернуться к очереди' : 'Архив проверок'}>
            {isArchive ? <IcBack /> : <IcArchive />}
          </button>,
          document.querySelector('.main-content') || document.body
        )}
      </div>

      {loading ? null : (
        <div className={`stu-swipe-win${swiping ? ' anim' : ''}`} style={{ height: swipeH ? `${swipeH}px` : undefined }}>
          <div className={`stu-swipe${swiping ? ' anim' : ''}`} style={{ transform: isArchive ? 'translateX(-50%)' : 'none' }}>
            <div className="stu-swipe-pane" ref={queuePaneRef}>{shelf(works, false)}</div>
            <div className="stu-swipe-pane" ref={archivePaneRef}>{shelf(archive, true)}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewPage;
